/**
 * Mapeo único solicitud (PASPE / remisión / detalle) → valores texto para fichas.
 * Centraliza rutas alternativas del API y evita duplicar lógica entre formularios y PDF.
 */

export const extraerTexto = (valor) => {
  if (valor === undefined || valor === null) return '';
  if (typeof valor === 'string' || typeof valor === 'number' || typeof valor === 'boolean') {
    return String(valor);
  }
  if (Array.isArray(valor)) {
    return valor.map(extraerTexto).filter(Boolean).join(', ');
  }
  if (typeof valor === 'object') {
    return extraerTexto(
      valor.nombre ??
        valor.label ??
        valor.descripcion ??
        valor.valor ??
        valor.value ??
        valor.codigo ??
        valor.nombre_relacion ??
        valor.detalle ??
        ''
    );
  }
  return '';
};

const obtenerPrimeroDisponible = (solicitud, rutas = []) => {
  if (!solicitud) return '';
  for (const ruta of rutas) {
    const valor = ruta?.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), solicitud);
    const texto = extraerTexto(valor);
    if (texto !== '') {
      return texto;
    }
  }
  return '';
};

const normalizarSiNo = (valor) => {
  const texto = extraerTexto(valor).trim();
  if (!texto) return '';
  const normalizado = texto.toLowerCase();
  if (['si', 'sí', 's', 'true', '1', 'x', 'checked', 'activar'].includes(normalizado)) return 'SI';
  if (['no', 'n', 'false', '0', 'unchecked', 'no activar'].includes(normalizado)) return 'NO';
  return texto.toUpperCase() === 'SI' || texto.toUpperCase() === 'NO' ? texto.toUpperCase() : texto;
};

/**
 * Campos del paso "Información general" de fichas de seguimiento (texto plano / labels).
 */
export const buildPrefillCamposGenerales = (solicitud) => ({
  programa: obtenerPrimeroDisponible(solicitud, [
    'programa.nombre',
    'programa',
    'programa_dependencia.nombre',
    'programa_dependencia',
    'usuario_registro.programa.nombre',
    'usuario_registro.programa',
  ]),
  semestre: obtenerPrimeroDisponible(solicitud, ['semestre', 'usuario_registro.semestre']),
  fecha_nacimiento: obtenerPrimeroDisponible(solicitud, [
    'fecha_nacimiento',
    'fecha_nac',
    'usuario_registro.fecha_nacimiento',
  ]),
  estado_civil: obtenerPrimeroDisponible(solicitud, [
    'estado_civil.nombre',
    'estado_civil',
    'usuario_registro.estado_civil.nombre',
    'usuario_registro.estado_civil',
  ]),
  sexo_biologico: obtenerPrimeroDisponible(solicitud, [
    'genero.nombre',
    'genero',
    'usuario_registro.genero.nombre',
    'usuario_registro.genero',
    'sexo_biologico.nombre',
    'sexo_biologico',
    'usuario_registro.sexo_biologico.nombre',
    'usuario_registro.sexo_biologico',
  ]),
  identidad_genero: obtenerPrimeroDisponible(solicitud, [
    'identidad_genero.nombre',
    'identidad_genero',
    'usuario_registro.identidad_genero.nombre',
    'usuario_registro.identidad_genero',
  ]),
  orientacion_sexual: obtenerPrimeroDisponible(solicitud, [
    'orientacion.nombre',
    'orientacion',
    'orientacion_sexual.nombre',
    'orientacion_sexual',
    'usuario_registro.orientacion.nombre',
    'usuario_registro.orientacion',
    'usuario_registro.orientacion_sexual.nombre',
    'usuario_registro.orientacion_sexual',
  ]),
  direccion_actual: obtenerPrimeroDisponible(solicitud, [
    'direccion_actual',
    'direccion',
    'usuario_registro.direccion',
  ]),
  barrio: obtenerPrimeroDisponible(solicitud, [
    'barrio',
    'usuario_registro.barrio',
    'usuario_registro.lugar_residencia',
  ]),
  lugar_procedencia: obtenerPrimeroDisponible(solicitud, [
    'lugar_procedencia',
    'usuario_registro.lugar_procedencia',
  ]),
  departamento: obtenerPrimeroDisponible(solicitud, [
    'departamento.nombre',
    'departamento',
    'usuario_registro.departamento.nombre',
    'usuario_registro.departamento',
  ]),
  municipio: obtenerPrimeroDisponible(solicitud, [
    'municipio.nombre',
    'municipio',
    'ciudad.nombre',
    'ciudad',
    'usuario_registro.ciudad.nombre',
    'usuario_registro.ciudad',
    'usuario_registro.municipio.nombre',
    'usuario_registro.municipio',
  ]),
  correo_electronico: obtenerPrimeroDisponible(solicitud, [
    'correo_electronico',
    'correo_personal',
    'correo',
    'usuario_registro.correo_personal',
    'usuario_registro.correo',
  ]),
  celular: obtenerPrimeroDisponible(solicitud, [
    'celular',
    'telefono',
    'usuario_registro.celular',
    'usuario_registro.telefono',
  ]),
  contacto_emergencia: obtenerPrimeroDisponible(solicitud, [
    'nombre_contacto_emergencia',
    'contacto_emergencia_1',
    'contacto_emergencia',
    'usuario_registro.contacto_emergencia',
  ]),
  celular_contacto_emergencia: obtenerPrimeroDisponible(solicitud, [
    'contacto_emergencia',
    'telefono_contacto',
    'celular_contacto_1',
    'celular_contacto_emergencia',
    'telefono_contacto_emergencia',
    'usuario_registro.celular_contacto_emergencia',
  ]),
  // Solo nombres / FK con detalle; nunca .id primero (ganaba un número y el select quedaba en blanco).
  parentesco: obtenerPrimeroDisponible(solicitud, [
    'parentesco_detalle.nombre',
    'parentesco_contacto_1_detalle.nombre',
    'parentesco_contacto_1.nombre',
    'parentesco.nombre',
    'parentesco',
    'parentesco_contacto_1',
    'parentesco_detalle.id',
    'parentesco.id',
  ]),
});

export const buildPrefillCamposSaludComunes = (solicitud) => ({
  nombre_eps: obtenerPrimeroDisponible(solicitud, [
    'nombre_eps',
    'eps.nombre',
    'eps',
    'usuario_registro.eps.nombre',
    'usuario_registro.eps',
    'usuario_registro.nombre_eps',
  ]),
  sisben: normalizarSiNo(
    obtenerPrimeroDisponible(solicitud, ['sisben', 'usuario_registro.sisben'])
  ),
  no_vinculado: obtenerPrimeroDisponible(solicitud, ['no_vinculado', 'usuario_registro.no_vinculado']),
  grupo_priorizado: obtenerPrimeroDisponible(solicitud, [
    'grupo_priorizado.nombre',
    'grupo_priorizado',
    'usuario_registro.grupo_priorizado.nombre',
    'usuario_registro.grupo_priorizado',
  ]),
  se_auto_reconoce_grupo_priorizado: obtenerPrimeroDisponible(solicitud, [
    'se_auto_reconoce_grupo_priorizado',
    'otro_grupo',
    'usuario_registro.se_auto_reconoce_grupo_priorizado',
  ]),
  certificado_grupo_priorizado: normalizarSiNo(
    obtenerPrimeroDisponible(solicitud, [
      'certificado_grupo_priorizado',
      'usuario_registro.certificado_grupo_priorizado',
    ])
  ),
});

const MOTIVO_ORIGENES = [
  'motivo_consulta',
  'motivo',
  'descripcion_motivo_consulta',
  'motivo_asesoria_comunicativa',
  'motivo_consulta_cognitiva',
  'motivo_consulta_cognitiva_especifica',
  'motivo_crisis',
  'descripcion_problematica',
];

const buildPrefillCamposMotivo = (solicitud, tipoFicha) => {
  const motivoBase = obtenerPrimeroDisponible(solicitud, MOTIVO_ORIGENES);
  if (!motivoBase) return {};

  const tipo = String(tipoFicha || '').toLowerCase();
  if (tipo === 'bien_apr') {
    return {
      motivo_consulta_cognitiva: motivoBase,
      motivo_consulta_cognitiva_especifica: motivoBase,
    };
  }
  if (tipo === 'bien_com') {
    return { motivo_asesoria_comunicativa: motivoBase };
  }
  if (tipo === 'bien_cris') {
    return { motivo_crisis: motivoBase };
  }
  if (tipo === 'ficha_unica') {
    return { motivo_consulta: motivoBase };
  }
  if (tipo === 'bien_pas') {
    return {
      motivo_consulta: motivoBase,
      descripcion_motivo_consulta: motivoBase,
    };
  }
  return {};
};

export const buildPrefillCamposFichaEspecificos = (solicitud, tipoFicha) => ({
  ...buildPrefillCamposSaludComunes(solicitud),
  ...buildPrefillCamposMotivo(solicitud, tipoFicha),
});

/** Campos del primer paso de ficha única (prefill desde solicitud / persona). */
export const buildPrefillFichaUnicaSalud = (solicitud) => ({
  ...buildPrefillCamposSaludComunes(solicitud),
  ideacion_suicida: extraerTexto(solicitud?.ideacion_suicida),
  intento_suicida: extraerTexto(solicitud?.intento_suicida),
  conducta_suicida: extraerTexto(solicitud?.conducta_suicida),
  deseo_muerte: extraerTexto(solicitud?.deseo_muerte),
  deseo_danarse: extraerTexto(solicitud?.deseo_danarse),
  plan_suicida: extraerTexto(solicitud?.plan_suicida),
  trataste_suicidarte: extraerTexto(solicitud?.trataste_suicidarte),
});

export const esValorVacio = (v) =>
  v === undefined || v === null || String(v).trim() === '';

/** Orden de prefill en el paso "Riesgos y salud" de ficha única. */
export const CAMPOS_PREFILL_FICHA_UNICA_SALUD = [
  'nombre_eps',
  'sisben',
  'no_vinculado',
  'grupo_priorizado',
  'se_auto_reconoce_grupo_priorizado',
  'certificado_grupo_priorizado',
  'ideacion_suicida',
  'intento_suicida',
  'conducta_suicida',
  'deseo_muerte',
  'deseo_danarse',
  'plan_suicida',
  'trataste_suicidarte',
];
